import React, { useState, useEffect } from 'react';
import { 
  Container, Table, Badge, Form, InputGroup, Button, Alert, 
  Row, Col, Card, Modal, Spinner
} from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { userService, departmentService } from '../../services/api';

const UserList = () => {
  const { currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // New employee form state
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    username: '',
    email: '',
    password: '',
    role: 'employee',
    department_id: ''
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers();
      if (response.data && response.data.success) {
        const userData = response.data.data || [];
        setUsers(userData);
        setFilteredUsers(userData);
        setError('');
      } else {
        setError('Failed to load users data');
        setUsers([]);
        setFilteredUsers([]);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await departmentService.getAllDepartments();
      if (response.data && response.data.success) {
        setDepartments(response.data.data || []);
      } else {
        console.error('Failed to load departments data');
        setDepartments([]);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
      setDepartments([]);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, []);

  useEffect(() => {
    // Apply filters and search
    let result = [...users];
    
    // Apply role filter
    if (roleFilter !== 'all') {
      result = result.filter(user => user.role === roleFilter);
    }
    
    // Apply search term
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      result = result.filter(user => 
        user.username.toLowerCase().includes(term) || 
        user.email.toLowerCase().includes(term)
      );
    }
    
    setFilteredUsers(result);
  }, [users, roleFilter, searchTerm]);

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin': return 'success';
      case 'employee': return 'info';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  // Handle employee form input changes
  const handleEmployeeChange = (e) => {
    const { name, value } = e.target;
    setNewEmployee(prev => ({ ...prev, [name]: value }));
  };

  // Handle adding a new employee
  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      const response = await userService.registerUser(newEmployee);
      if (response.data && response.data.success) {
        setShowEmployeeModal(false);
        setSuccess('Employee added successfully!');
        // Reset form
        setNewEmployee({
          username: '',
          email: '',
          password: '',
          role: 'employee',
          department_id: ''
        });
        // Refresh user list
        fetchUsers();
      } else {
        setError(response.data?.message || 'Failed to add employee');
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error adding employee:', err);
      setError(err.response?.data?.message || 'Failed to add employee');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Loading users...</span>
      </div>
    );
  }

  return (
    <Container fluid className="px-4 py-3 users-section">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0 fw-bold text-primary">Users</h2>
        
        {isAdmin && currentUser?.role === 'admin' && (
          <Button 
            variant="primary" 
            onClick={() => setShowEmployeeModal(true)}
            className="d-flex align-items-center shadow-sm"
          >
            <i className="fas fa-user-plus me-2"></i> Add New Employee
          </Button>
        )}
      </div>
      
      {error && (
        <Alert variant="danger" className="d-flex align-items-center mb-4 shadow-sm">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert variant="success" className="d-flex align-items-center mb-4 shadow-sm">
          <i className="fas fa-check-circle me-2"></i>
          {success}
        </Alert>
      )}
      
      <Card className="shadow-sm mb-4 border-0">
        <Card.Body className="p-3">
          <Row>
            <Col md={8}>
              <InputGroup className="mb-3 mb-md-0">
                <InputGroup.Text className="bg-light border-end-0">
                  <i className="fas fa-search text-muted"></i>
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by username or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border-start-0 bg-light"
                />
                {searchTerm && (
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => setSearchTerm('')}
                  >
                    <i className="fas fa-times"></i>
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={4}>
              <Form.Select 
                value={roleFilter} 
                onChange={(e) => setRoleFilter(e.target.value)}
                className="bg-light"
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="employee">Employee</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {filteredUsers.length === 0 ? (
        <Card className="text-center py-5 border-0 shadow-sm">
          <Card.Body>
            <i className="fas fa-users text-muted mb-3" style={{ fontSize: '2rem' }}></i>
            <h5>No users found</h5>
            <p className="text-muted">Try adjusting your search or filter criteria</p>
          </Card.Body>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="px-3 py-3 border-0">Username</th>
                    <th className="px-3 py-3 border-0">Email</th>
                    <th className="px-3 py-3 border-0">Role</th>
                    <th className="px-3 py-3 border-0">Created At</th>
                    <th className="px-3 py-3 border-0">Last Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user.id} className="align-middle">
                      <td className="px-3 py-3">
                        <div className="d-flex align-items-center">
                          <div className="avatar-circle me-2" style={{ 
                            backgroundColor: user.role === 'admin' ? '#d1e7dd' : '#cfe2ff',
                            color: user.role === 'admin' ? '#0f5132' : '#084298',
                            width: '36px', 
                            height: '36px', 
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center', 
                            justifyContent: 'center',
                            fontWeight: 'bold'
                          }}>
                            {user.username.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="fw-medium">{user.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-muted">{user.email}</td>
                      <td className="px-3 py-3">
                        <Badge 
                          bg={getRoleBadgeVariant(user.role)}
                          className="rounded-pill px-3 py-2"
                        >
                          {user.role === 'admin' ? 'Admin' : 'Employee'}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-muted">{formatDate(user.created_at)}</td>
                      <td className="px-3 py-3 text-muted">{formatDate(user.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}
      
      {/* Add Employee Modal */}
      <Modal 
        show={showEmployeeModal} 
        onHide={() => setShowEmployeeModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">Add New Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <Form onSubmit={handleAddEmployee}>
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={newEmployee.username}
                onChange={handleEmployeeChange}
                required
                className="bg-light"
                placeholder="Enter username"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={newEmployee.email}
                onChange={handleEmployeeChange}
                required
                className="bg-light"
                placeholder="Enter email address"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={newEmployee.password}
                onChange={handleEmployeeChange}
                required
                className="bg-light"
                placeholder="Create a password"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                name="role"
                value={newEmployee.role}
                onChange={handleEmployeeChange}
                className="bg-light"
              >
                <option value="employee">Employee</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Department</Form.Label>
              <Form.Select
                name="department_id"
                value={newEmployee.department_id}
                onChange={handleEmployeeChange}
                className="bg-light"
              >
                <option value="">Select Department</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            
            <div className="d-grid gap-2 mt-4">
              <Button variant="primary" type="submit" className="py-2">
                Add Employee
              </Button>
              <Button 
                variant="light" 
                onClick={() => setShowEmployeeModal(false)}
                className="py-2"
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default UserList;
